'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import { compile } from '@/lib/expr';

const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2'];

type View = { cx: number; cy: number; scale: number };

function niceStep(raw: number): number {
  const p = 10 ** Math.floor(Math.log10(raw));
  const m = raw / p;
  return (m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10) * p;
}

const label = (v: number, step: number) => {
  const digits = Math.max(0, -Math.floor(Math.log10(step)));
  return Number(v.toFixed(digits)).toLocaleString('en-US', { maximumFractionDigits: digits });
};

function roots(fn: (x: number) => number, a: number, b: number): number[] {
  const out: number[] = [];
  const n = 800;
  let px = a;
  let py = fn(px);
  for (let i = 1; i <= n; i += 1) {
    const x = a + ((b - a) * i) / n;
    const y = fn(x);
    if (Number.isFinite(py) && Number.isFinite(y)) {
      if (y === 0) out.push(x);
      else if (py * y < 0) {
        let lo = px;
        let hi = x;
        for (let k = 0; k < 60; k += 1) {
          const mid = (lo + hi) / 2;
          if (fn(lo) * fn(mid) <= 0) hi = mid;
          else lo = mid;
        }
        const r = (lo + hi) / 2;
        // A sign change across a pole (tan x at π/2) is not a root.
        if (Math.abs(fn(r)) < 1e-6 * Math.max(1, Math.abs(py), Math.abs(y))) out.push(r);
      }
    }
    px = x;
    py = y;
  }
  return out.filter((r, i) => i === 0 || Math.abs(r - out[i - 1]) > 1e-7).slice(0, 8);
}

export default function BrowserGraphingCalculator({ config }: { config?: { toolSlug?: string } }) {
  const [inputs, setInputs] = useState<string[]>(['x^2 - 2', 'sin(x)']);
  const [view, setView] = useState<View>({ cx: 0, cy: 0, scale: 50 });
  const [hover, setHover] = useState<{ x: number; px: number; py: number } | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 640, h: 420 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const lastPinch = useRef<number | null>(null);
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'graphing-calculator', '').catch(() => undefined);
  };

  const compiled = useMemo(() => inputs.map((src) => (src.trim() ? compile(src) : null)), [inputs]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      setSize({ w, h: Math.round(Math.min(560, Math.max(300, w * 0.66))) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function zoomAt(px: number, py: number, k: number) {
    setView((v) => {
      const scale = Math.min(1e6, Math.max(1e-4, v.scale * k));
      const wx = v.cx + (px - size.w / 2) / v.scale;
      const wy = v.cy - (py - size.h / 2) / v.scale;
      return { scale, cx: wx - (px - size.w / 2) / scale, cy: wy + (py - size.h / 2) / scale };
    });
  }

  // Wheel zoom must be non-passive so the page does not scroll.
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      touch();
      const r = el.getBoundingClientRect();
      zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0015));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  useEffect(() => {
    const c = canvas.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = size.w * dpr;
    c.height = size.h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const { w, h } = size;
    const { cx, cy, scale } = view;
    const toPx = (x: number) => w / 2 + (x - cx) * scale;
    const toPy = (y: number) => h / 2 - (y - cy) * scale;
    const fg = getComputedStyle(c).color || '#111';

    ctx.clearRect(0, 0, w, h);
    const step = niceStep(80 / scale);
    const x0 = cx - w / 2 / scale;
    const x1 = cx + w / 2 / scale;
    const y0 = cy - h / 2 / scale;
    const y1 = cy + h / 2 / scale;

    // Grid: minor (step/5) then major.
    ctx.lineWidth = 1;
    for (const [s, alpha] of [
      [step / 5, 0.06],
      [step, 0.14],
    ] as const) {
      ctx.strokeStyle = fg;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      for (let x = Math.ceil(x0 / s) * s; x <= x1; x += s) {
        ctx.moveTo(Math.round(toPx(x)) + 0.5, 0);
        ctx.lineTo(Math.round(toPx(x)) + 0.5, h);
      }
      for (let y = Math.ceil(y0 / s) * s; y <= y1; y += s) {
        ctx.moveTo(0, Math.round(toPy(y)) + 0.5);
        ctx.lineTo(w, Math.round(toPy(y)) + 0.5);
      }
      ctx.stroke();
    }
    // Axes and labels (clamped to the edge when the axis is off screen).
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    const ax = Math.min(h - 1, Math.max(0, toPy(0)));
    const ay = Math.min(w - 1, Math.max(0, toPx(0)));
    ctx.moveTo(0, Math.round(ax) + 0.5);
    ctx.lineTo(w, Math.round(ax) + 0.5);
    ctx.moveTo(Math.round(ay) + 0.5, 0);
    ctx.lineTo(Math.round(ay) + 0.5, h);
    ctx.stroke();
    ctx.globalAlpha = 0.75;
    ctx.fillStyle = fg;
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let x = Math.ceil(x0 / step) * step; x <= x1; x += step) {
      if (Math.abs(x) < step / 2) continue;
      ctx.fillText(label(x, step), toPx(x), Math.min(h - 14, ax + 3));
    }
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let y = Math.ceil(y0 / step) * step; y <= y1; y += step) {
      if (Math.abs(y) < step / 2) continue;
      ctx.fillText(label(y, step), Math.max(28, ay - 4), toPy(y));
    }
    ctx.globalAlpha = 1;

    // Curves: sample every half pixel, break the path at gaps and near-vertical jumps.
    compiled.forEach((c2, i) => {
      if (!c2 || !('fn' in c2)) return;
      const fn = c2.fn;
      ctx.strokeStyle = COLORS[i % COLORS.length];
      ctx.lineWidth = 2.25;
      ctx.beginPath();
      let pen = false;
      let prevY = 0;
      for (let px = 0; px <= w; px += 0.5) {
        const y = fn(x0 + px / scale);
        const py = toPy(y);
        if (!Number.isFinite(y) || Math.abs(py) > h * 20) {
          pen = false;
          continue;
        }
        if (pen && Math.abs(py - prevY) > h * 1.5) pen = false;
        if (pen) ctx.lineTo(px, py);
        else ctx.moveTo(px, py);
        pen = true;
        prevY = py;
      }
      ctx.stroke();
    });

    if (hover) {
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = fg;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(hover.px, 0);
      ctx.lineTo(hover.px, h);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      compiled.forEach((c2, i) => {
        if (!c2 || !('fn' in c2)) return;
        const y = c2.fn(hover.x);
        if (!Number.isFinite(y)) return;
        ctx.fillStyle = COLORS[i % COLORS.length];
        ctx.beginPath();
        ctx.arc(hover.px, toPy(y), 4.5, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  }, [compiled, view, size, hover]);

  const x0 = view.cx - size.w / 2 / view.scale;
  const x1 = view.cx + size.w / 2 / view.scale;
  const fmt = (v: number) =>
    Math.abs(v) < 1e-10
      ? '0'
      : Math.abs(v) >= 1e6 || Math.abs(v) < 1e-4
        ? v.toExponential(4)
        : Number(v.toFixed(6)).toString();

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    touch();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    const prev = pointers.current.get(e.pointerId);
    if (prev) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 2) {
        const [a, b] = [...pointers.current.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (lastPinch.current)
          zoomAt((a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top, d / lastPinch.current);
        lastPinch.current = d;
        return;
      }
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setView((v) => ({ ...v, cx: v.cx - dx / v.scale, cy: v.cy + dy / v.scale }));
    }
    setHover({ px, py: e.clientY - r.top, x: view.cx + (px - size.w / 2) / view.scale });
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) lastPinch.current = null;
  };

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Graphing</span>
        <h2 className="tool-live-title">Graphing calculator</h2>
        <p className="tool-live-subtitle">
          Type functions of x. Drag to move, scroll or pinch to zoom, hover to trace.
        </p>
      </div>
      {/* One column: the graph gets the full width, inputs sit above it. */}
      <div className="tool-live-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <section className="tool-live-uploader">
          {inputs.map((src, i) => {
            const c2 = compiled[i];
            return (
              <label className="tool-live-field tool-live-field-full" key={i}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 999,
                      background: COLORS[i % COLORS.length],
                      display: 'inline-block',
                    }}
                  />
                  y{inputs.length > 1 ? `${i + 1}` : ''} =
                </span>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input
                    aria-invalid={Boolean(c2 && 'error' in c2)}
                    onChange={(e) => {
                      touch();
                      setInputs((l) => l.map((v, j) => (j === i ? e.target.value : v)));
                    }}
                    spellCheck={false}
                    style={{ flex: 1, minWidth: 0, fontFamily: 'ui-monospace, monospace' }}
                    type="text"
                    value={src}
                  />
                  {inputs.length > 1 ? (
                    <button
                      aria-label={`Remove function ${i + 1}`}
                      className="button button-secondary"
                      onClick={() => setInputs((l) => l.filter((_, j) => j !== i))}
                      type="button"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
                {c2 && 'error' in c2 ? (
                  <small style={{ color: '#dc2626' }}>{c2.error}</small>
                ) : null}
              </label>
            );
          })}
          <div className="tool-live-actions">
            {inputs.length < COLORS.length ? (
              <button
                className="button button-secondary"
                onClick={() => setInputs((l) => [...l, ''])}
                type="button"
              >
                Add function
              </button>
            ) : null}
            <button
              className="button button-secondary"
              onClick={() => zoomAt(size.w / 2, size.h / 2, 1.5)}
              type="button"
            >
              Zoom in
            </button>
            <button
              className="button button-secondary"
              onClick={() => zoomAt(size.w / 2, size.h / 2, 1 / 1.5)}
              type="button"
            >
              Zoom out
            </button>
            <button
              className="button button-secondary"
              onClick={() => setView({ cx: 0, cy: 0, scale: 50 })}
              type="button"
            >
              Reset view
            </button>
          </div>
          <p className="tool-live-panel-note">
            Write 2x, 3sin(x), (x+1)(x−1), x^2 or x², sqrt(x), abs(x), ln(x), log(x), e^x, pi.
            Implicit multiplication works; 1/2x means (1/2)·x.
          </p>
        </section>
        <section className="tool-live-preview">
          <div ref={wrap} style={{ width: '100%' }}>
            <canvas
              aria-label="Graph of the functions"
              onPointerCancel={onPointerUp}
              onPointerDown={onPointerDown}
              onPointerLeave={() => setHover(null)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              ref={canvas}
              role="img"
              style={{
                width: size.w,
                height: size.h,
                maxWidth: '100%',
                display: 'block',
                touchAction: 'none',
                cursor: 'grab',
                borderRadius: 8,
                border: '1px solid rgba(127,127,127,0.35)',
              }}
            />
          </div>
          {hover ? (
            <p className="tool-live-panel-note" style={{ fontVariantNumeric: 'tabular-nums' }}>
              x = {fmt(hover.x)}
              {compiled.map((c2, i) =>
                c2 && 'fn' in c2 ? (
                  <span key={i} style={{ color: COLORS[i % COLORS.length], marginLeft: '0.75rem' }}>
                    y{inputs.length > 1 ? i + 1 : ''} = {fmt(c2.fn(hover.x))}
                  </span>
                ) : null,
              )}
            </p>
          ) : null}
          {compiled.map((c2, i) => {
            if (!c2 || !('fn' in c2)) return null;
            const rs = roots(c2.fn, x0, x1);
            const yi = c2.fn(0);
            return (
              <p
                className="tool-live-panel-note"
                key={i}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                <strong style={{ color: COLORS[i % COLORS.length] }}>
                  y{inputs.length > 1 ? i + 1 : ''}:
                </strong>{' '}
                {Number.isFinite(yi) ? `y-intercept (0, ${fmt(yi)})` : 'no y-intercept'}
                {'; '}
                {rs.length
                  ? `x-intercepts in view: ${rs.map((r) => fmt(r)).join(', ')}`
                  : 'no x-intercepts in view'}
              </p>
            );
          })}
        </section>
      </div>
    </div>
  );
}
